import React from 'react';
import {
  createAlignPlugin,
  createBoldPlugin,
  createHeadingPlugin,
  createItalicPlugin,
  createParagraphPlugin,
  createLinkPlugin,
  createExitBreakPlugin,
  createFontBackgroundColorPlugin,
  createFontColorPlugin,
  createFontSizePlugin,
  createStrikethroughPlugin,
  createUnderlinePlugin,
  createHorizontalRulePlugin,
  createMediaEmbedPlugin,
  createNodeIdPlugin,
  createIndentPlugin,
  createResetNodePlugin,
  createTrailingBlockPlugin,
  createTablePlugin,
  createListPlugin,
  createDeserializeDocxPlugin,
  ELEMENT_PARAGRAPH,
  ELEMENT_TR,
  ELEMENT_TD,
  ELEMENT_TH,
  ELEMENT_H1,
  ELEMENT_H2,
  ELEMENT_H3,
  ELEMENT_H4,
  ELEMENT_H5,
  ELEMENT_H6,
  MARK_BOLD,
  MARK_ITALIC,
  MARK_UNDERLINE,
  MARK_STRIKETHROUGH,
} from '@udecode/plate';
import { createPlugins } from '@udecode/plate-core';
// import { createBlockSelectionPlugin, BlockSelectable } from '@udecode/plate-selection';
import { createDndPlugin } from '@udecode/plate-dnd';

import { createEnhancedNormalizerPlugin } from '../plugins/normalizers/createEnhancedNormalizerPlugin';
import { createErrorHandlingPlugin } from '../plugins/error/createErrorHandlingPlugin';
import { createMCENormalizerPlugin } from '../plugins/normalizers/createMCENormalizerPlugin';
import { createFontFamilyPlugin } from '../plugins/fonts/createFontFamilyPlugin';
import { createTodoListPlugin } from '../plugins/todo-list/createTodoListPlugin';
import { tablePlugin, tableRowPlugin, tableCellPlugin } from '../plugins/table/tablePlugin';
import { createImagePlugin } from '../plugins/media/images/createImagePlugin';
import { createPageBreakPlugin } from '../plugins/page-break/createPageBreakPlugin';
import { indentPlugin } from '../plugins/indent/indentPlugin';
import { listPlugin, listPluginByKey } from '../plugins/list/listPlugin';
import { linkPlugin } from '../plugins/link/linkPlugin';
import { mediaEmbedPlugin } from '../plugins/media/embed/mediaEmbedPlugin';
import { fontColorPlugin } from '../plugins/fonts/fontColorPlugin';
import { fontSizePlugin } from '../plugins/fonts/fontSizePlugin';
import { fontBackgroundColorPlugin } from '../plugins/fonts/fontBackgroundColorPlugin';
import { LinkFloatingToolbar } from '../plugins/link/LinkFloatingToolbar';
import { plateUI, serializePlateUI } from '../plateUI';
import { VALID_ALIGNMENT_TYPES } from '../plugins/alignment/constants';
import { SerializedTodoElement } from '../plugins/todo-list/ToDoListElement';
import { createNoteLayoutPlugin } from '../plugins/layout/note-layout/createNoteLayoutPlugin';
import { createFakeSelectionPlugin } from '../plugins/fake-selection/createFakeSelectionPlugin';
import { resetNodePlugin } from '../plugins/reset-node/resetNodePlugin';
import { HeadingElement } from '../plugins/heading/HeadingElement';
import { HrElement } from '../plugins/hr/HrElement';
import { ParagraphElement } from '../plugins/paragraph/ParagraphElement';

import '../components/popup/floating.scss';

/* eslint-disable react/jsx-max-props-per-line */
const serializedHeadings = {
  [ELEMENT_H1]: { serializeHtml: (props) => <HeadingElement as="h1" {...props} /> },
  [ELEMENT_H2]: { serializeHtml: (props) => <HeadingElement as="h2" {...props} /> },
  [ELEMENT_H3]: { serializeHtml: (props) => <HeadingElement as="h3" {...props} /> },
  [ELEMENT_H4]: { serializeHtml: (props) => <HeadingElement as="h4" {...props} /> },
  [ELEMENT_H5]: { serializeHtml: (props) => <HeadingElement as="h5" {...props} /> },
  [ELEMENT_H6]: { serializeHtml: (props) => <HeadingElement as="h6" {...props} /> },
};
/* eslint-enable react/jsx-max-props-per-line */

const components = plateUI;

export const plugins = createPlugins(
  [
    createEnhancedNormalizerPlugin(),
    createDeserializeDocxPlugin(),
    createMCENormalizerPlugin(),
    createTrailingBlockPlugin({ level: 0, type: ELEMENT_PARAGRAPH }),
    createErrorHandlingPlugin(),
    createResetNodePlugin(resetNodePlugin),
    createParagraphPlugin(),
    createHeadingPlugin({}, serializedHeadings),
    createAlignPlugin({
      inject: {
        props: {
          validTypes: VALID_ALIGNMENT_TYPES,
        },
      },
    }),
    createImagePlugin(),
    createMediaEmbedPlugin(mediaEmbedPlugin),
    createListPlugin(listPlugin, listPluginByKey),
    createTodoListPlugin(),
    createLinkPlugin({ ...linkPlugin, renderAfterEditable: LinkFloatingToolbar }),
    createTablePlugin({
      ...tablePlugin,
      options: { enableMerging: true },
    },
    {
      [ELEMENT_TR]: tableRowPlugin, [ELEMENT_TD]: tableCellPlugin, [ELEMENT_TH]: tableCellPlugin,
    }),

    createBoldPlugin(),
    createItalicPlugin(),
    createUnderlinePlugin(),
    createStrikethroughPlugin(),

    createExitBreakPlugin(),
    createFontBackgroundColorPlugin(),
    createFontColorPlugin(),
    createFontFamilyPlugin(),
    createFontSizePlugin(),
    createHorizontalRulePlugin({ serializeHtml: (props) => <HrElement {...props} /> }),
    createPageBreakPlugin(),

    createIndentPlugin(indentPlugin),

    createNodeIdPlugin(),
    createDndPlugin({ options: { enableScroller: true } }),
    createFakeSelectionPlugin(),
    createNoteLayoutPlugin(),
  ],
  { components: components() },
);

export const serializedPlugins = [
  createMCENormalizerPlugin(),
  createParagraphPlugin({ serializeHtml: props => <ParagraphElement {...props} /> }),
  createHeadingPlugin({}, serializedHeadings),
  createAlignPlugin({
    inject: {
      props: {
        validTypes: VALID_ALIGNMENT_TYPES,
      },
    },
  }),
  createImagePlugin(),
  createMediaEmbedPlugin(mediaEmbedPlugin),
  createListPlugin(listPlugin, listPluginByKey),
  createTodoListPlugin({
    serializeHtml: SerializedTodoElement,
  }),
  createLinkPlugin(linkPlugin),
  createTablePlugin(tablePlugin, {
    [ELEMENT_TR]: tableRowPlugin,
    [ELEMENT_TD]: tableCellPlugin,
    [ELEMENT_TH]: tableCellPlugin,
  }),

  createBoldPlugin({ serializeHtml: ({ children, leaf }) =>
    leaf[MARK_BOLD] && !!leaf.text ? <strong>{children}</strong> : children,
  }),
  createItalicPlugin({ serializeHtml: ({ children, leaf }) =>
    leaf[MARK_ITALIC] && !!leaf.text ? <em>{children}</em> : children,
  }),
  createUnderlinePlugin({ serializeHtml: ({ children, leaf }) =>
    leaf[MARK_UNDERLINE] && !!leaf.text ? <u>{children}</u> : children,
  }),
  createStrikethroughPlugin({ serializeHtml: ({ children, leaf }) =>
    leaf[MARK_STRIKETHROUGH] && !!leaf.text ? <s>{children}</s> : children,
  }),

  createExitBreakPlugin(),
  createFontBackgroundColorPlugin(fontBackgroundColorPlugin),
  createFontColorPlugin(fontColorPlugin),
  createFontFamilyPlugin(),
  createFontSizePlugin(fontSizePlugin),
  createHorizontalRulePlugin({ serializeHtml: (props) => <HrElement {...props} /> }),
  createPageBreakPlugin(),

  createIndentPlugin(indentPlugin),

  createNodeIdPlugin(),
  createDndPlugin({ options: { enableScroller: true } }),

  createNoteLayoutPlugin(),

  { components: serializePlateUI },
];
